import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Filter, Building2, Calendar, TrendingUp, AlertTriangle, CheckCircle, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import axios from 'axios';
import { CreateCaseDialog } from '../components/subscription/CreateCaseDialog';
import { SubscriptionSummaryCards } from '../components/subscription/SubscriptionSummaryCards';
import { CaseListToolbar, CaseListHeader, CASE_COLS, applyCaseFilters } from '../components/subscription/CaseListToolbar';

const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const API_BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Para formatı
const formatCurrency = (amountInKurus) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amountInKurus / 100);
};

// Durum badge renkleri
const getStatusColor = (status) => {
  const colors = {
    'Aranacak': 'bg-blue-100 text-blue-700',
    'Takipte': 'bg-yellow-100 text-yellow-700',
    'Teklif verildi': 'bg-purple-100 text-purple-700',
    'Ertelendi': 'bg-orange-100 text-orange-700',
    'Yenilemeyecek': 'bg-red-100 text-red-700',
    'Yenileme yok': 'bg-gray-100 text-gray-600',
    'Kısmi yenilendi': 'bg-amber-100 text-amber-700',
    'Tam yenilendi': 'bg-green-100 text-green-700'
  };
  return colors[status] || 'bg-gray-100 text-gray-600';
};

export default function SubscriptionCaseList() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [showCreate, setShowCreate] = useState(false);
  const [renewalFilter, setRenewalFilter] = useState('all');
  const [collectionFilter, setCollectionFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'smart', dir: 'desc' });

  useEffect(() => {
    loadCases();
    loadSummary();
  }, [statusFilter, monthFilter, yearFilter]);

  const periodParams = () =>
    (monthFilter !== 'all' ? `&period_month=${monthFilter}` : '') + (yearFilter !== 'all' ? `&period_year=${yearFilter}` : '');

  const loadCases = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE}/api/subscription-management/cases?token=${token}${periodParams()}`;
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      
      const response = await axios.get(url);
      setCases(response.data);
    } catch (error) {
      console.error('Error loading cases:', error);
      toast.error('Dosyalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${API_BASE}/api/subscription-management/summary?token=${token}${periodParams()}`;
      
      const response = await axios.get(url);
      setSummary(response.data);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const filteredCases = cases.filter(c => 
    c.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.representative.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const years = [-1, 0, 1].map(i => String(new Date().getFullYear() + i));
  const visibleCases = applyCaseFilters(filteredCases, { renewalFilter, collectionFilter, sort });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Abonelik Yönetimi</h1>
          <p className="text-sm text-gray-500">Abonelik yenileme takip sistemi</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="import-excel-btn" variant="outline" onClick={() => navigate('/subscription-management/import')} className="flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" />Excel Yükle
          </Button>
          <Button data-testid="create-case-btn" onClick={() => setShowCreate(true)} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />Yeni Dosya
          </Button>
        </div>
      </div>
      <CreateCaseDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={(id) => navigate(`/subscription-management/${id}`)} />

      {/* Özet Kartları */}
      {summary && <SubscriptionSummaryCards summary={summary} onOpenCase={(id) => navigate(`/subscription-management/${id}`)} />}

      {/* Filtreler ve sıralama */}
      <CaseListToolbar
        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        renewalFilter={renewalFilter} setRenewalFilter={setRenewalFilter}
        collectionFilter={collectionFilter} setCollectionFilter={setCollectionFilter}
        count={visibleCases.length}
      />

      {/* Durum Özeti */}
      {summary?.status_counts && (
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(summary.status_counts).map(([status, count]) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                statusFilter === status 
                  ? 'ring-2 ring-offset-2 ring-blue-500' 
                  : ''
              } ${getStatusColor(status)}`}
            >
              {status}: {count}
            </button>
          ))}
          {statusFilter !== 'all' && (
            <button
              onClick={() => setStatusFilter('all')}
              className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              Tümünü Göster
            </button>
          )}
        </div>
      )}

      {/* Filtreler */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Müşteri veya temsilci ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger className="w-full md:w-40" data-testid="case-month-filter">
            <SelectValue placeholder="Ay" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Aylar</SelectItem>
            {MONTHS.map((m, i) => (
              <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger className="w-full md:w-32" data-testid="case-year-filter">
            <SelectValue placeholder="Yıl" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Yıllar</SelectItem>
            {years.map(y => (
              <SelectItem key={y} value={y}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Dosya Listesi */}
      {visibleCases.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Henüz abonelik dosyası yok</p>
            <p className="text-sm text-gray-400 mt-1">Yeni dosya eklemek için sağ üstteki Yeni Dosya butonunu kullanın</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <CaseListHeader sort={sort} setSort={setSort} />
          {visibleCases.map(caseItem => {
            const isOverdue = caseItem.operation_status !== 'Yenilemeyecek' && caseItem.renewal_status !== 'Tam yenilendi' && caseItem.next_followup_date && new Date(caseItem.next_followup_date) < new Date();
            
            return (
              <Card 
                key={caseItem.id} 
                data-testid={`case-row-${caseItem.id}`}
                className={`cursor-pointer hover:shadow-md transition-shadow ${isOverdue ? 'border-red-200 bg-red-50' : ''}`}
                onClick={() => navigate(`/subscription-management/${caseItem.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Sol: Müşteri Bilgileri */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-800 truncate">{caseItem.customer_name}</h3>
                        {isOverdue && (
                          <span className="flex items-center gap-1 text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded">
                            <AlertTriangle className="w-3 h-3" />
                            Gecikmiş
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 text-sm text-gray-500">
                        <span>{caseItem.period}</span>
                        <span>•</span>
                        <span>{caseItem.representative}</span>
                      </div>
                    </div>

                    {/* Orta: Durumlar */}
                    <div className={`flex gap-2 ${CASE_COLS.status}`}>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(caseItem.operation_status)}`}>
                        {caseItem.operation_status}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(caseItem.renewal_status)}`}>
                        {caseItem.renewal_status}
                      </span>
                    </div>

                    {/* Sağ: Rakamlar */}
                    <div className="flex gap-6 text-right">
                      <div className={CASE_COLS.num}>
                        <p className="text-xs text-gray-400 md:hidden">Cihaz</p>
                        <p className="font-semibold text-gray-700">
                          {caseItem.total_renewed_devices}/{caseItem.source_device_count}
                        </p>
                      </div>
                      <div className={CASE_COLS.num}>
                        <p className="text-xs text-gray-400 md:hidden">Gerçekleşen</p>
                        <p className="font-semibold text-green-600">{formatCurrency(caseItem.total_renewed_amount)}</p>
                      </div>
                      <div className={CASE_COLS.num}>
                        <p className="text-xs text-gray-400 md:hidden">Tahsilat</p>
                        <p className="font-semibold text-emerald-600">{formatCurrency(caseItem.total_collected)}</p>
                      </div>
                      <div className={CASE_COLS.num}>
                        <p className="text-xs text-gray-400 md:hidden">Bakiye</p>
                        <p className={`font-semibold ${caseItem.remaining_balance > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                          {formatCurrency(caseItem.remaining_balance)}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
